import React, { useState } from 'react';
import { MessageSquarePlus, MessageSquare, Search, ChevronLeft, Trash2, RefreshCw } from 'lucide-react';
import { Conversation } from '../types/chat';
import Marquee from "react-fast-marquee";

interface LeftPanelProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onConversationSelect: (conversation: Conversation) => void;
  onNewChat: () => void;
  activeConversation: Conversation | null;
  conversations: Conversation[];
  hasNewChat: boolean;
  loading: boolean;
  sending: boolean;
  onDeleteConversation?: (conversationId: string) => Promise<void>;
  onSyncConversations?: () => Promise<void>;
}

interface ConversationGroup {
  label: string;
  conversations: Conversation[];
}

const TypingIndicator = () => (
  <div className="flex space-x-1">
    <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
    <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
    <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full typing-dot" />
  </div>
);

const groupConversationsByDate = (conversations: Conversation[]): ConversationGroup[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups: { [key: string]: Conversation[] } = {
    'Pinned': [],
    'Shared to you': []
  };

  conversations.forEach(conversation => {
    // First handle pinned conversations
    if (conversation.isPinned) {
      groups['Pinned'].push(conversation);
      return;
    }
    
    // Then handle shared conversations (that are shared to the user)
    if (conversation.isSharedTo) {
      groups['Shared to you'].push(conversation);
      return;
    }

    const date = new Date(conversation.updated_at);
    date.setHours(0, 0, 0, 0);

    let label: string;
    if (date.getTime() === today.getTime()) {
      label = 'Today';
    } else if (date.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    } else {
      label = date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }

    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(conversation);
  });

  // Remove empty groups
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return Object.entries(groups)
    .map(([label, conversations]) => ({
      label,
      conversations: conversations.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    }))
    .sort((a, b) => {
      // Always keep Pinned at the very top
      if (a.label === 'Pinned') return -1;
      if (b.label === 'Pinned') return 1;
      
      // Keep Shared to you right after Pinned
      if (a.label === 'Shared to you') return -1;
      if (b.label === 'Shared to you') return 1;
      
      // Then sort by date groups
      if (a.label === 'Today') return -1;
      if (b.label === 'Today') return 1;
      if (a.label === 'Yesterday') return -1;
      if (b.label === 'Yesterday') return 1;
      
      // For other date groups, sort by the most recent conversation in each group
      return new Date(b.conversations[0].created_at).getTime() - 
             new Date(a.conversations[0].created_at).getTime();
    });
};

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  isLoading: boolean;
  isSending: boolean;
  onClick: () => void;
  onDelete: () => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  isLoading,
  isSending,
  onClick,
  onDelete
}) => {
  const [slideX, setSlideX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't start dragging if we're touching the delete button when it's active
    if (slideComplete && (e.target as HTMLElement).closest('[data-testid="delete-button"]')) {
      return;
    }
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if we're clicking the delete button when it's active
    if (slideComplete && (e.target as HTMLElement).closest('[data-testid="delete-button"]')) {
      return;
    }
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setSlideX(Math.min(0, Math.max(-100, diff))); // Limit sliding to -100px
    e.preventDefault(); // Prevent scrolling while dragging
  };

  // This function is no longer needed as we're using global event listeners
  // Keep it as a placeholder for the touch version
  const handleMouseMove = (e: React.MouseEvent) => {
    // No longer used - handled by global event listener
  };

  // Track if the slide is complete (fully slid to show delete button)
  const [slideComplete, setSlideComplete] = useState(false);

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (slideX < -50) {
      setSlideX(-100); // Snap to delete button
      setSlideComplete(true); // Mark slide as complete
    } else {
      setSlideX(0); // Snap back
      setSlideComplete(false); // Reset slide complete state
    }
  };

  // This function is no longer needed as we're using global event listeners
  const handleMouseUp = () => {
    // No longer used - handled by global event listener
  };

  const handleClick = () => {
    if (Math.abs(slideX) < 5) { // Only trigger click if not sliding
      onClick();
      setSlideX(0); // Reset slide on click
      setSlideComplete(false); // Reset slide complete state
    }
  };

  // Add event listeners to document for mouse move and up when dragging starts
  React.useEffect(() => {
    if (isDragging) {
      // Add global event listeners when dragging starts
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const currentX = e.clientX;
        const diff = currentX - startX;
        setSlideX(Math.min(0, Math.max(-100, diff))); // Limit sliding to -100px
        e.preventDefault(); // Prevent other events while dragging
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        if (slideX < -50) {
          setSlideX(-100); // Snap to delete button
          setSlideComplete(true); // Mark slide as complete
        } else {
          setSlideX(0); // Snap back
          setSlideComplete(false); // Reset slide complete state
        }
      };

      // Add global event listeners
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      // Clean up
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, startX, slideX]);

  return (
    <div 
      className="relative overflow-hidden touch-manipulation"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
    >
      <button
        onClick={handleClick}
        data-testid="conversation-item"
        className={`
          w-full px-4 py-3 rounded-lg text-left transition-all duration-200 transform
          ${isActive
            ? 'text-blue-700 dark:text-blue-300 relative overflow-hidden'
            : 'hover:bg-gradient-to-r hover:from-blue-500/10 hover:via-purple-500/10 hover:to-pink-500/10 dark:hover:from-blue-500/20 dark:hover:via-purple-500/20 dark:hover:to-pink-500/20 text-gray-700 dark:text-gray-300'
          }
        `}
        style={{ 
          transform: `translateX(${slideX}px)`
        }}
      >
        {isActive && (
          <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
            {/* Base layer with glass shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent bg-[length:200%_100%] animate-glass-shine rounded-lg"></div>
            
            {/* Second layer with horizontal movement */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 via-transparent to-purple-500/15 bg-[length:200%_100%] animate-gradient-x rounded-lg"></div>
            
            {/* Third layer with vertical movement */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/15 to-transparent bg-[length:100%_200%] animate-gradient-y rounded-lg"></div>
            
            {/* Fourth layer with diagonal movement */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 bg-[length:200%_200%] animate-ambient-move rounded-lg opacity-70"></div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium truncate flex-1 min-w-0">
            
            {isActive && conversation.title.length > 24 ? <Marquee 
            gradient={false} 
            speed={50} 
            className="text-blue-600 dark:text-blue-400 font-semibold"
          >
            {conversation.title}
          </Marquee>:conversation.title }
          </div>
          <div className="flex items-center gap-2">
            {conversation.isSharedBy && (
              <div className="flex-shrink-0 text-blue-600 dark:text-blue-400 transform rotate-90">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
              </div>
            )}
            {isActive && (isLoading || isSending) && (
              <div className="flex-shrink-0">
                <TypingIndicator />
              </div>
            )}
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {new Date(conversation.updated_at).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
          })}
        </div>
      </button>
      {/* Invisible overlay to prevent interaction with delete button until slide is complete */}
      {slideX < 0 && !slideComplete && (
        <div 
          className="absolute top-0 right-0 bottom-0 w-[100px] z-10"
          style={{ opacity: 0 }}
        />
      )}
      
      {/* Only render the delete button when slid enough */}
      {slideX < -20 && (
        <button
          data-testid="delete-button"
          onClick={(e) => {
            // Only allow delete action if slide is complete
            if (slideComplete) {
              e.stopPropagation();
              onDelete();
              setSlideX(0);
              setSlideComplete(false);
            }
          }}
          className={`absolute top-0 right-0 bottom-0 w-[100px] flex items-center justify-center text-white transition-colors ${
            slideComplete ? 'bg-red-500 hover:bg-red-600' : 'bg-red-400'
          }`}
          style={{ 
            opacity: Math.min(1, Math.abs(slideX) / 100),
            pointerEvents: slideComplete ? 'auto' : 'none'
          }}
        >
          <Trash2 size={20} />
        </button>
      )}
    </div>
  );
};

export const LeftPanel: React.FC<LeftPanelProps> = ({
  sidebarOpen,
  toggleSidebar,
  searchQuery,
  setSearchQuery,
  onConversationSelect,
  onNewChat,
  activeConversation,
  conversations,
  hasNewChat,
  loading,
  sending,
  onDeleteConversation,
  onSyncConversations
}) => {
  // Check if a conversation is active based on URL and conversation ID
  const isConversationActive = React.useCallback((conversation: Conversation): boolean => {
    // Check if we're in a shared conversation view
    const isSharedView = window.location.pathname.includes('/convo/shared/');
    
    if (isSharedView) {
      // In shared view, only the shared conversation matching the URL should be active
      return Boolean(conversation.isSharedTo && window.location.pathname.includes(`/convo/shared/${conversation.id}`));
    } else {
      // In regular view, use the activeConversation state
      return activeConversation?.id === conversation.id || false;
    }
  }, [activeConversation]);
  // Filter conversations based on search query
  const filteredConversations = React.useMemo(() => {
    if (!searchQuery) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv => 
      conv.title.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  // Group filtered conversations by date
  const conversationGroups = React.useMemo(() => 
    groupConversationsByDate(filteredConversations),
    [filteredConversations]
  );

  const handleConversationClick = (conversation: Conversation) => {
    if (conversation.id !== activeConversation?.id) {
      onConversationSelect(conversation);
    }
  };

  return (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      <div 
        className={`
          fixed md:relative z-30 h-full bg-white dark:bg-gray-800 shadow-lg flex flex-col
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-80 translate-x-0' : 'w-0 -translate-x-full overflow-hidden'}
        `}
      >
        <div className="flex-shrink-0 p-4 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Conversations</h2>
            </div>
            <div className="flex items-center space-x-2">
              {onSyncConversations && (
                <button 
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  onClick={onSyncConversations}
                  title="Sync conversations with server"
                >
                  <RefreshCw size={18} />
                </button>
              )}
              <button 
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors md:hidden"
                onClick={toggleSidebar}
              >
                <ChevronLeft size={20} />
              </button>
            </div>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 pl-10 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm dark:text-white dark:placeholder-gray-400"
            />
            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800 scrollbar-custom">
          {conversationGroups.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </div>
          ) : (
            <div className="p-2">
              {conversationGroups.map((group) => (
                <div key={group.label} className="mb-4">
                  <div className="px-2 mb-2">
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {group.label}
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {group.conversations.map((conversation) => (
                    <ConversationItem
                      key={conversation.id}
                      conversation={conversation}
                      isActive={isConversationActive(conversation)}
                      isLoading={loading}
                      isSending={sending}
                      onClick={() => handleConversationClick(conversation)}
                      onDelete={() => onDeleteConversation?.(conversation.id)}
                    />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onNewChat}
            disabled={hasNewChat}
            className="w-full flex items-center justify-center space-x-2 bg-blue-600 dark:bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-gradient-to-r hover:from-blue-500/10 hover:via-purple-500/10 hover:to-pink-500/10 dark:hover:from-blue-500/20 dark:hover:via-purple-500/20 dark:hover:to-pink-500/20 transition-colors font-medium shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 dark:disabled:hover:bg-blue-500"
          >
            <MessageSquarePlus size={20} />
            <span>New Convo</span>
          </button>
        </div>
      </div>
    </>
  );
};
